import axios, {
  AxiosError,
  AxiosRequestConfig,
  AxiosRequestHeaders,
  InternalAxiosRequestConfig,
} from 'axios';
import qs from 'qs';

import { authCache } from 'api/cache';
import { tenantCache } from 'api/tenantCache';
import { ApiError } from 'container/models';

import { ApiPaths, CONTENT_TYPE } from '../constants';
import { authHeaderName, externalAuthHeaderName, tenantHeader } from './config';
import transform from './transform';

const { REACT_APP_API_BASE: apiBaseURL } = import.meta.env;
const { REACT_APP_COMMIT_SHA: COMMIT_SHA } = import.meta.env;

const LoggedOutErr: ApiError = {
  status: 401,
  statusText: 'Unauthorized (Client)',
  data: [{ code: 0, description: 'Client is not logged in' }],
};

const MissingTenantErr: ApiError = {
  status: 401,
  statusText: 'Unauthorized (Client)',
  data: [{ code: 0, description: 'Client tenant is not set' }],
};

export default function httpRequest<SuccessPayload>(
  config: AxiosRequestConfig
) {
  return httpClient.request<SuccessPayload>(config);
}

export const httpClient = axios.create({
  headers: {
    'Content-Type': CONTENT_TYPE.APPLICATION_JSON,
  },
  baseURL: apiBaseURL as string,
  // eslint-disable-next-line @typescript-eslint/no-unsafe-return
  transformRequest: (data) => transformRequest(data),
  // eslint-disable-next-line @typescript-eslint/no-unsafe-return
  transformResponse: (data) => transformResponse(data as string),
  paramsSerializer,
});

const formatForAllowlist = (url: URL) => url.host + url.pathname;

/** a set of hostname+absolute paths that are allowed on the server without being
 * logged in (incl. /api/v1/ prefix for forward compatibility) - see {@link formatForAllowlist}.
 *
 * Add things with {@link AddToLoggedOutRouteAllowlist}
 *
 * this might need to be converted to a different structure later if we need to
 * support logged-out routes with parameters (ex: /processes/:releaseOrDraftProcessId/).
 * if this happens, there's now a URLPattern to make it easy.
 */
const loggedOutRouteAllowlist = new Set();
/**
 *  Based on 'ApiPaths.external.form.uploadFile._' as example
 *  this implementation is missing two aspects to be more generic:
 *    - it assumes api base (httpClient.getUri with default config which is api)
 *    - it does not take into account parameter-based paths which require Regex
 */
export const AddToLoggedOutRouteAllowlist = (url: string) =>
  loggedOutRouteAllowlist.add(
    formatForAllowlist(new URL(httpClient.getUri({ url })))
  );

// these are the known static paths that are allowed; some will be added later based on server responses (just application configuration for now)
[
  ApiPaths.auth.login(),
  ApiPaths.auth.register(),
  // ApiPaths.auth.refreshToken(), // this doesn't require an auth token, but you do still have to be logged in, so...?
  ApiPaths.auth.validateInvitationOtt(),
  ApiPaths.applicationConfiguration(),
  ApiPaths.external.form.validate._(),
  ApiPaths.external.form.submit._(),
  ApiPaths.auth.forgotPassword(),
  ApiPaths.auth.validateForgotPassword(),
  ApiPaths.auth.restorePassword(),
].forEach(AddToLoggedOutRouteAllowlist);

/** hostname+absolute paths allowed on the server without knowing a tenant ID */
const unknownTenantAllowlist = new Set();
const AddToUnknownTenantAllowlist = (url: string) =>
  unknownTenantAllowlist.add(
    formatForAllowlist(new URL(httpClient.getUri({ url })))
  );

[
  ApiPaths.users.info._(),
  ApiPaths.generateFreshdeskToken._(),
  ApiPaths.tenants._(),
].forEach(AddToUnknownTenantAllowlist);

const generateInterceptorHeaders = (
  config: InternalAxiosRequestConfig
): Record<string, string> => {
  return generateHeaders(httpClient.getUri(config), config.headers);
};

/**
 * Generates headers for requests based on destination URL.
 *
 * For WorkOptima hosts, this returns:
 * - the client build ID (commit SHA)
 * - current timestamp (to check for clock skew)
 * - auth token if logged in
 *
 * It will also throw an error if the user is trying to access something on a WorkOptima domain and the path is not on the unauthenticated allowlist.
 */
export const generateHeaders = (
  url: string,
  requestHeaders?: AxiosRequestHeaders
): Record<string, string> => {
  const headers: Record<string, string> = {};

  const parsedURL = new URL(url);

  // check hostname (port-agnostic) to see if it's any of our trusted subdomains
  // match: "localhost" exactly, or "workoptima.com" exactly, or "(something).workoptima.com", but not "something-workoptima.com" (not our domain)
  if (parsedURL.hostname.match(/(^|\.)workoptima\.com$|^localhost$/i)) {
    if (authCache.loggedIn) {
      headers[authHeaderName] = authCache.authToken;

      if (tenantCache.tenantId) headers[tenantHeader] = tenantCache.tenantId;
      else if (
        unknownTenantAllowlist.has(formatForAllowlist(parsedURL)) ||
        requestHeaders?.has(externalAuthHeaderName)
      ) {
        // ok
      } else {
        // eslint-disable-next-line @typescript-eslint/only-throw-error
        throw MissingTenantErr;
      }
    } else if (
      loggedOutRouteAllowlist.has(formatForAllowlist(parsedURL)) ||
      requestHeaders?.has(externalAuthHeaderName)
    ) {
      // ok if it's allowed url or request already has external auth header
    } else {
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw LoggedOutErr;
    }

    if (COMMIT_SHA) headers['Client-Build-Id'] = COMMIT_SHA as string;

    headers['Client-Timestamp'] = Date.now().toString();
  }

  return headers;
};

/**
 * Applies rewrites to URLs:
 * - use HTTPS for all WorkOptima URLs (seemingly only a problem for weird dev cases)
 */
export const rewriteUrl = (url: string): string => {
  const parsedURL = new URL(url);

  if (
    parsedURL.hostname.match(/(^|\.)workoptima\.com$/i) &&
    parsedURL.protocol === 'http:'
  ) {
    parsedURL.protocol = 'https:';
    return parsedURL.toString();
  }

  return url;
};

httpClient.interceptors.request.use((config) => {
  config.headers = config.headers.concat(generateInterceptorHeaders(config));

  config.url = rewriteUrl(httpClient.getUri(config));

  return config;
});

export function transformRequest(data: any, stopPaths?: string[]) {
  if (!data) {
    return data;
  }
  if (data instanceof FormData || data instanceof File) {
    return data;
  }

  return JSON.stringify(
    transform(data, {
      toSnake: true,
      stopPaths,
    })
  );
}

export function transformResponse(data: string, stopPaths?: string[]) {
  try {
    return transform(JSON.parse(data), {
      stopPaths,
    });
  } catch (e) {
    return data;
  }
}

function paramsSerializer(params: {}) {
  return qs.stringify(params, {
    arrayFormat: 'brackets',
  });
}

export const transformerConfigFactory = (opts: {
  requestStopPaths?: string[];
  responseStopPaths?: string[];
}): AxiosRequestConfig => ({
  transformRequest: (data) => transformRequest(data, opts.requestStopPaths),
  transformResponse: (data) => transformResponse(data, opts.responseStopPaths),
});

/** Wrap an Axios request in a handler that retries once if the request fails due to expired tokens. */
export async function handleExpiredTokens<Data>(
  requestFactory: () => Promise<Data>
): Promise<Data> {
  // refresh our token before flight if needed
  if (authCache.authTokenExpired) await refreshOrDie();

  // if we're currently trying to refresh the token, wait for that to resolve first.
  await authCache.currentTokenRefresh;

  try {
    return await requestFactory();
  } catch (e) {
    // we only care about one specific kind of response here, throw all others
    if (!(e instanceof AxiosError) || e.response?.status !== 401) throw e;

    await refreshOrDie();

    return requestFactory();
  }

  async function refreshOrDie(): Promise<void> {
    try {
      await authCache.refreshTokens();
    } catch (e) {
      authCache.clear();
      window.location.reload(); // execution after this point is unpredictable; clearly things *can* happen but it's undocumented as to whether or not they should
      throw e; // assuming we're still alive, throw to crash things and prevent a bad request
    }
  }
}
